import React, { useState, useEffect, useRef, useMemo } from "react";
import { useHistory, useRouteMatch } from 'react-router-dom';

import cx from 'classnames';
import _ from 'lodash';
import Truncate from "react-truncate";
import dateformat from "dateformat";

import Tooltip from "components/Tooltip";
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";
import { convertDatesToTimezone } from "services/report.service";
import styles from '../report.module.scss';

export function MetricsHeader() {
  return (
    <div className={cx(styles.metricsHeaderContainer)}>
      <div className={styles.header}>
        <span className={cx(styles.name, styles.nameColumn)}>Name</span>
        <span className={cx(styles.name, styles.viewsColumn)}>Views</span>
        <span className={cx(styles.name, styles.visitorsColumn)}>Visitors</span>
        <span className={cx(styles.name, styles.leadsColumn)}>Leads</span>
        <span className={cx(styles.name, styles.timeSpentColumn)}>Total Time</span>
        <span className={cx(styles.name, styles.avgTimeSpentColumn)}>Avg. Time</span>
        <span className={cx(styles.name, styles.bounceRateColumn)}>Bounce Rate</span>
      </div>
    </div>
  )
}

export function WorkspaceMetricsHeader(props) {
  return (
    <div className={cx(styles.workspaceMetricsHeaderContainer)}>
      <div className={styles.header}>
        <span className={cx(styles.name, styles.nameColumn)}>{props.label}</span>
        <span className={cx(styles.name, styles.viewsColumn)}>Views</span>
        <span className={cx(styles.name, styles.visitorsColumn)}>Visitors</span>
        <span className={cx(styles.name, styles.leadsColumn)}>Leads</span>
        <span className={cx(styles.name, styles.timeSpentColumn)}>Total Time</span>
        <span className={cx(styles.name, styles.avgTimeSpentColumn)}>Avg. Time</span>
        <span className={cx(styles.name, styles.bounceRateColumn)}>Bounce Rate</span>
      </div>
    </div>
  )
}

export function SingleTile(props) {
  return (
    <div className={cx(styles.tiles, styles.singleTile)}>
      <img alt="tileIcon" className={props.iconClassName} src={props.path} />
      <div className={styles.downloadContainer}>
        <h1 className={styles.title}>{props.title}</h1>
        <span className={styles.description}>{props.description}</span>
      </div>
      <h1 className={styles.value}>{props.value}</h1>
    </div>
  )
}

export function HorizontalTile(props) {
  return (
    <div className={cx(styles.tiles, styles.horizontalTile)}>
      <img alt="tileIcon" className={props.iconClassName} src={props.path} />
      <div className={styles.horizontalTileWrapper}>
        <div className={styles.leftHorizontal}>
          <div>
            <div className={styles.title}>{props.title}</div>
            <div className={styles.description}>{props.description}</div>
          </div>
          <div className={styles.description}>{props.timeLabelTotalTime}</div>
          <div className={styles.value}>{props.value}</div>
        </div>

        <div className={styles.divider}></div>

        <div className={styles.rightHorizontal}>
          <div className={styles.title}>{props.secondTitle}</div>
          <div className={styles.description}>{props.secondDesc}</div>
          <div className={styles.description}>{props.timeLabelAverageTime}</div>
          <div className={styles.value}>{props.secondValue}</div>
        </div>

      </div>
    </div>
  )
}


export function HorizontalRightTile(props) {
  const blocks = [
    {
      title: "Total Time",
      description: "Spent",
      value: props.totalTime,
      label: props.totalTimeLabel,
    },
  ];
  if (!props.splitView) {
    blocks.push({
      title: "Avg Time",
      description: "Spent",
      value: props.avgTime,
      label: props.avgTimeLabel,
    });
  }
  return (
    <>
      <div className={styles.horizontalTile}>
        <img
          alt="tileIcon"
          className={styles.timeSpentIcon}
          src={"/images/reports/timespent.svg"}
        />
        <div className={styles.rightContent}>
          {blocks.map((block) => (
            <div className={styles.block} key={block.title}>
              <div className={styles.upper}>
                <h1 className={styles.heading}>{block.title}</h1>
                <p className={styles.subHeading}>{block.description}</p>
                <span className={styles.subHeading}>{block.label}</span>
              </div>
              <h1 className={styles.value}>{block.value}</h1>
            </div>
          ))}
        </div>
      </div>
      {props.splitView && (
        <div className={styles.horizontalTile}>
          <img
            alt="tileIcon"
            className={styles.timeSpentIcon}
            src={"/images/reports/average-time.svg"}
          />
          <div className={styles.rightContent}>
            {blocks.map((block) => (
              <div className={styles.block} key={block.title}>
                <div className={styles.upper}>
                  <h1 className={styles.heading}>{'Average Time'}</h1>
                  <p className={styles.subHeading}>{'Spent'}</p>
                  <span className={styles.subHeading}>{props.avgTimeLabel}</span>
                </div>
                <h1 className={styles.value}>{props.avgTime}</h1>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

export function VerticalTile({ path, title, description, value }) {
  return (
    <div className={styles.insightBlock}>
      <img alt="tileIcon" className={styles.icon} src={path} />
      <div className={styles.content}>
        <div className={styles.upper}>
          <h1 className={styles.title}>{title}</h1>
          <span className={styles.description}>{description}</span>
        </div>
        <h1>{value ?? <span className={styles.placeholder}>--</span>}</h1>
      </div>
    </div>
  );
}

export function VisitorProfileView({data, showProfile, reportDate, type}) {
  const history = useHistory();
  let { url } = useRouteMatch();
  const [tooltip, setTooltip] = useState(false);
  const handleTooltip = (truncated) => {
    setTooltip(truncated);
  }

  const renderVisitorName = (data) => {
    var name = type === "sessions" ? `${_.get(data, 'firstName') || '-'} ${_.get(data, 'lastName') || ""} ` : `${_.get(data, 'firstName') || '-'}` ;
   
    return (
      <Tooltip placement="top" content={name} disabled={!tooltip}>
        <Truncate onTruncate={handleTooltip} width={150} lines={1}>
          {name}
        </Truncate>
      </Tooltip>);
  }

  const renderInitials = (name) => {
    let firstName = _.get(name, 'firstName') || '-';
    let lastName = _.get(name, 'lastName') || '';

    let initial = '';

    if (firstName) {
      initial = firstName.charAt(0);
    }

    if (lastName) {
      initial = initial + lastName.charAt(0);
    }

    return initial.toUpperCase();
  }

  const renderOrganizationName = (data) => {
    if (data.organization) {
      return data.organization.name;
    }

    if (data.contact && data.contact.company) {
      return capitalizeFirstLetter(data.contact.company);
    }

    return renderCompanyNameFromEmail(data.contact.email);
  }

  const renderCompanyNameFromEmail = (email) => {
    const domain = email.split("@")[1];
    const company = domain.split(".")[0];
    return capitalizeFirstLetter(company)
  }

  const capitalizeFirstLetter = (value) => {
    if (value) {
      value = value.charAt(0).toUpperCase() + value.slice(1);
    }

    return value;
  }

  const handleProfileClick = () => {
    if(!showProfile) {
      return
    }

    history.push({
      pathname: `${url}/profile`, 
      state: {
        reportDate: {...convertDatesToTimezone(reportDate)},
        profileId: _.get(data, 'contact.id'),
        data: data
      }
    });
  };

  return (
    <div className={cx(styles.visitorsProfile, styles.wrapper, styles.visitorColumn)}>
      <div onClick={handleProfileClick} className={styles.visitorInitials}>{renderInitials(type == "sessions" ? data.visitor : data.contact)}</div>
      <div className={styles.visitorsProfileName}>
        <span onClick={handleProfileClick} className={styles.wrapper}>{renderVisitorName(type == "sessions" ? data.visitor : data.contact)}</span>
        {type === "sessions" &&
          <>
            <span className={cx(styles.email, styles.wrapper)}>
              <Tooltip placement="top" content={data.visitor.email} disabled={!tooltip}>
                <Truncate onTruncate={handleTooltip} width={135} lines={1}>
                  {data.visitor.email}
                </Truncate>
              </Tooltip>
            </span>
            <span className={styles.lastVisitedDate}>
              {dateformat(data.lastVisited, 'dd mmm yyyy')}
            </span>
          </>
        }

      </div>
    </div>
  )
}

export function SessionAnalyticsHeader() {
  return (
    <div className={cx(styles.sessionAnalyticsHeaderContainer)}>
      <div className={styles.header}>
        <span className={cx(styles.name, styles.visitorDetailColum)}>Visitor Details<br />& Date</span>
        <span className={cx(styles.name, styles.sourceColumn)}>Source, Medium <br />& Page landed on</span>
        <span className={cx(styles.name, styles.locationColumn)}>Geography<br />& Device</span>
        <span className={cx(styles.name, styles.timespentColumn)}>Time &<br />Completion %</span>
        {/* <span className={cx(styles.name, styles.pipelineColumn)}>Pipeline Value</span> */}
        <span className={cx(styles.name, styles.pageAnalyticsColumn)}>Page analytics</span>
      </div>
    </div>
  )
}

export function LeadAnalyticsHeader({ header = [], storyboardId, isWorkspace = false }) {
  const savedFields = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem(storyboardId)) || [];
    } catch {
      return [];
    }
  }, [storyboardId]);

  const defaultFields = header.map((lead) => ({
    fieldId: lead.field,
    uid: lead.uid,
    name: lead.name,
    visible: true,
  }));
  const [fields, setFields] = useState(savedFields.length ? savedFields : defaultFields);

  useEffect(() => {
    if (!header.length) return;

    const updatedFields = savedFields
      .filter((field) =>
        header.some((lead) => lead.uid === field.fieldId || lead.field === field.fieldId)
      )
      .map((field) => {
        const match = header.find(
          (lead) => lead.uid === field.fieldId || lead.field === field.fieldId
        );
        return {
          ...field,
          name: match ? match.name : field.name,
        };
      });

    const finalFields = updatedFields.length ? updatedFields : defaultFields;

    if (JSON.stringify(fields) !== JSON.stringify(finalFields)) {
      setFields(finalFields);
      localStorage.setItem(storyboardId, JSON.stringify(finalFields));
    }
  }, [header]);

  return (
    <div className={cx(styles.leadsAnalyticsHeaderContainer)}>
      <div className={styles.header}>
        {fields.map((header) => (
          <span
          key={header.uid || header.fieldId}
          className={cx(styles.name, styles.leadColumn, { [styles.workspaceLeadColumn]: isWorkspace })}
          >
            <TruncatedTextWithTooltip content={header.name} maxWidth={150} placement='top'/>
          </span>
        ))}
      </div>
    </div>
  );
}

export function PageAnalyticsHeader() {
  return (
    <div className={cx(styles.pageReportHeaderContainer)}>
      <div className={styles.header}>
        <span className={cx(styles.name, styles.pageNameColumn)}>Page Name</span>
        <span className={cx(styles.name, styles.viewsColumn)}>Views</span>
        <span className={cx(styles.name, styles.visitorsColumn)}>Visitors</span>
        <span className={cx(styles.name, styles.timeSpentColumn)}>Total Time</span>
        <span className={cx(styles.name, styles.avgTimeSpentColumn)}>Avg. Time</span>
        <span className={cx(styles.name, styles.bounceRateColumn)}>Bounce Rate</span>
      </div>
    </div>
  )
}

export function InteractionAnalyticsHeader() {
  return (
    <div className={cx(styles.interactionReportHeaderContainer)}>
      <div className={styles.header}>
        <span className={cx(styles.name, styles.pageNameColumn)}>Name</span>
        <span className={cx(styles.name, styles.clicksColumn)}>Clicks</span>
        <span className={cx(styles.name, styles.leadsColumn)}>Leads</span>
      </div>
    </div>
  )
}

export function AssetAnalyticsHeader() {
  return (
    <div className={cx(styles.assetReportHeaderContainer)}>
      <div className={styles.header}>
        <span className={cx(styles.name, styles.assetColumn)}>Asset Name</span>
        <span className={cx(styles.name, styles.viewsColumn)}>Views</span>
        <span className={cx(styles.name, styles.visitorsColumn)}>Visitors</span>
        <span className={cx(styles.name, styles.avgTimeSpentColumn)}>Avg. Time Spent</span>
        <span className={cx(styles.name, styles.bounceColumn)}>Bounce Rate</span>
        <span className={cx(styles.name, styles.downloadColumn)}>Downloads</span>
        <span className={cx(styles.name, styles.trendsColumn)}>Trends</span>
      </div>
    </div>
  )
}

export function GeographicAnalyticsHeader() {
  return (
    <div className={cx(styles.geographicReportHeaderContainer)}>
      <div className={styles.header}>
        <span className={cx(styles.name, styles.locationColumn)}>Location</span>
        <span className={cx(styles.name, styles.viewsColumn)}>Views</span>
        <span className={cx(styles.name, styles.visitorsColumn)}>Visitors</span>
        <span className={cx(styles.name, styles.avgTimeSpentColumn)}>Avg. Time Spent</span>
        <span className={cx(styles.name, styles.timeSpentColumn)}>Total Time Spent</span>
        <span className={cx(styles.name, styles.pageAnalyticsColumn)}>Page Analytics</span>
      </div>
    </div>
  )
}

export function JourneyAnalyticsHeader() {
  return (
    <div className={cx(styles.journeyReportHeaderContainer)}>
      <div className={styles.header}>
        <span className={cx(styles.name, styles.locationColumn)}>Journey Name</span>
        <span className={cx(styles.name, styles.viewsColumn)}>Date of creation</span>
        <span className={cx(styles.name, styles.avgTimeSpentColumn)}>Views</span>
        <span className={cx(styles.name, styles.timeSpentColumn)}>Visitors</span>
        <span className={cx(styles.name, styles.timeSpentColumn)}>Leads</span>
        <span className={cx(styles.name, styles.pageAnalyticsColumn)}>Avg Completion %</span>
      </div>
    </div>
  )
}

export function TableInfo(props) {
  return (
    <div className={styles.tableInfo} >
      <div className={styles.tableTitle}>{props.name}
        <span className={styles.count}>({_.get(props.pagination, 'totalRecordsCount', 0)})</span>
      </div>
    </div>
  )
}

export function EmptyMessage({ videoId }) {
  const ref = useRef(null);

  const emptyMessages = [
    {
      video: "/images/telescopeEmptyMessage.mp4",
      content: <span>No sightings to report.<br />Swing by this telescope later?</span>
    },
    {
      video: "/images/sateliteEmptyMessage.mp4",
      content: <span>No song’s coming off<br />this blank space though</span>
    },
  ];

  const [emptyMessageContent, setEmptyMessageContent] = useState({})

  useEffect(() => {
    ref.current.play();
    getRandomValueFromArray(emptyMessages)
  }, []);

  const getRandomValueFromArray = (array) => {
    var value = array[Math.floor(Math.random() * array.length)];
    setEmptyMessageContent(value)
  }

  return (
    <div className={styles.emptyMessage}>
      <div className={cx(styles.loaderContainer)}>
        <div className={styles.loaderVideoWrapper}>
          <video ref={ref} className={styles.loaderMessageVideo} loop autoPlay={true} playsInline muted>
            <source src={emptyMessageContent.video} type="video/mp4" />
          </video>
          <label className={styles.loaderMessage}>{emptyMessageContent.content}</label>
        </div>
      </div>
    </div>
  )
}

export function LoadingMessage() {
  const ref = useRef(null);

  useEffect(() => {
    ref.current.play();
  }, []);

  const loadingMessages = [
    <span>Getting you the Sun,<br /> Moon, and Stars</span>,
    <span>Rummaging through the lunar <br /> craters to find your numbers</span>
  ];

  return (
    <div className={styles.emptyMessage}>
      <div className={cx(styles.loaderContainer)}>
        <div className={styles.loaderVideoWrapper}>
          <video ref={ref} className={styles.loaderMessageVideo} loop autoPlay={true} playsInline muted>
            <source src="/images/reportsLoader.mp4" type="video/mp4" />
          </video>
          <label className={styles.loaderMessage}>{loadingMessages[Math.floor(Math.random() * loadingMessages.length)]}</label>
        </div>
      </div>
    </div>
  )
}

export function getWidthForAnalyticsBar(data, leadAnalytics = false) {
  var width = 18;

  if (!data) {
    return width
  }
  
  if (data.length > 1) {
    width = 30;
  }

  if (data.length > 5) {
    width = 50;
  }

  if (data.length > 10) {
    width = 110;
    if (leadAnalytics) {
      width = 80;
    }
  }

  return width;
}
